import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { Field, reduxForm, getFormValues } from 'redux-form';
import map from 'lodash/map';
import debounce from 'lodash/debounce';
import { Page, PageHeader } from 'components';
import ListHeading from 'shared/components/ListHeading';
import Button from 'shared/components/Button';
import TextField from 'shared/components/TextField';
import { Thead, Tbody, Table, Th, Tr, Td } from 'shared/components/Table';
import AppContent from 'shared/components/AppContent';
import { dateToReadable, utcDateToLocal } from 'shared/services/date';
import Pagination from 'shared/components/Pagination';
import { fetchSurveys } from 'modules/Survey/reducers/list';
import { fetchDiscussions } from 'modules/Discussion/reducers/list';
import Select from 'react-select';
import apiService from 'shared/services/api'; // Make sure this is correctly imported and configured
import { SURVEY_STATUS_LABEL } from 'modules/Survey/constants';
import styles from './styles.module.scss';
import { lowerCase } from 'lodash';

const FORM_NAME = 'planning/LIST';

// Status options for the filter dropdown
const STATUS_OPTIONS = [
  { value: 'Live', label: 'Live' },
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Canceled', label: 'Canceled' }
];

const TYPE_OPTIONS = [
  { value: 'Survey', label: 'Survey' },
  { value: 'Chatbox', label: 'Chatbox' }
];

const TIME_OPTIONS = [
  { value: 'next_week', label: 'Next week' },
  { value: 'next_3_days', label: 'Next 3 days' },
  { value: 'today', label: 'Today' },
  { value: 'current_week', label: 'Current week' },
  { value: 'last_3_days', label: 'Last 3 days' },
  { value: 'last_week', label: 'Last week' },
  { value: 'custom', label: 'Custom range' }
];

interface NotificationDetail {
  pushNotificationTitle?: string;
  pushNotificationEnabled?: boolean;
  isLoading: boolean;
  error?: boolean;
}

interface ListProps {
  surveyData: {
    number: number;
    totalPages: number;
    content: any[];
  };
  discussionData: {
    number: number;
    totalPages: number;
    content: any[];
  };
  surveyStates: {
    isLoading: boolean;
    isLoaded: boolean;
    hasError: boolean;
  };
  discussionStates: {
    isLoading: boolean;
    isLoaded: boolean;
    hasError: boolean;
  };
  fetchSurveys: (params: any) => void;
  fetchDiscussions: (params: any) => void;
  history: any;
  selectedCountry: any;
}

interface ListState {
  term: string | undefined;
  perPage: number;
  sort: string;
  totalAnswers: Record<number, number>;
  isLoadingAnswers: boolean;
  statusFilter: string[];
  typeFilter: string[];
  agentFilter: string[];
  timeFilter: { label: string; value: string };
  customRange: { from: Date | null; to: Date | null };
  combinedData: Array<any>;
  loading: boolean;
  // --- NEW STATE FOR NOTIFICATION DETAILS ---
  notificationDetails: Record<string, NotificationDetail>;
}

class List extends PureComponent<ListProps, ListState> {
  state: ListState = {
    term: undefined,
    perPage: 15,
    sort: 'date_from,desc',
    totalAnswers: {},
    isLoadingAnswers: true,
    statusFilter: [],
    typeFilter: [],
    agentFilter: [],
    timeFilter: { label: 'Current week', value: 'current_week' },
    customRange: { from: null, to: null },
    combinedData: [],
    loading: true,
    // --- INITIALIZE NEW STATE ---
    notificationDetails: {}
  };

  componentDidMount() {
    const savedFilters = localStorage.getItem('planningFilters');
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      // Convert customRange.from and customRange.to back to Date objects
      if (filters.customRange) {
        filters.customRange.from = filters.customRange.from ? new Date(filters.customRange.from) : null;
        filters.customRange.to = filters.customRange.to ? new Date(filters.customRange.to) : null;
      }
      this.setState(filters, () => this.search({}));
    } else {
      this.search({});
    }
    // Note: Initial data might not be available here yet for fetching notifications.
    // It will be handled by componentDidUpdate.
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.selectedCountry !== this.props.selectedCountry) {
      this.search({});
    }

    // Fetch notification details when survey or discussion data changes
    if (prevProps.surveyData !== this.props.surveyData && this.props.surveyData?.content) {
      this.props.surveyData.content.forEach(item => this.fetchNotificationDetailIfNeeded(item, 'Survey'));
    }
    if (prevProps.discussionData !== this.props.discussionData && this.props.discussionData?.content) {
      this.props.discussionData.content.forEach(item => this.fetchNotificationDetailIfNeeded(item, 'Chatbox'));
    }

    // Combine and sort data when primary data or notification details update
    if (
      prevProps.surveyData !== this.props.surveyData ||
      prevProps.discussionData !== this.props.discussionData ||
      prevState.notificationDetails !== this.state.notificationDetails || // Re-combine if details changed
      prevState.totalAnswers !== this.state.totalAnswers // Re-combine if answers changed
    ) {
      this.combineAndSortData();
    }
    
    // loadTotalAnswers when surveyData content is available and has changed
    // This was original logic, check if it's still needed or if combineAndSortData handles it
    if (prevProps.surveyData !== this.props.surveyData && this.props.surveyData?.content?.length) {
      this.loadTotalAnswers(this.props.surveyData);
    }


    if (
      prevState.statusFilter !== this.state.statusFilter ||
      prevState.typeFilter !== this.state.typeFilter ||
      prevState.agentFilter !== this.state.agentFilter ||
      prevState.timeFilter !== this.state.timeFilter ||
      prevState.customRange !== this.state.customRange
    ) {
      localStorage.setItem('planningFilters', JSON.stringify({
        statusFilter: this.state.statusFilter,
        typeFilter: this.state.typeFilter,
        agentFilter: this.state.agentFilter,
        timeFilter: this.state.timeFilter,
        customRange: this.state.customRange
      }));
      // Filters changed, re-combine and filter data (already handled by combineAndSortData dependencies)
      this.combineAndSortData(); // Or ensure filters trigger a search which then triggers combineAndSortData
    }
  }

  // --- NEW FUNCTION TO FETCH NOTIFICATION DETAILS ---
  fetchNotificationDetailIfNeeded = async (item, itemType: 'Survey' | 'Chatbox') => {
    const itemId = item.id;
    const cacheKey = `${itemType.toLowerCase()}-${itemId}`;

    // If already fetched, loading, or has errored, don't refetch
    if (this.state.notificationDetails[cacheKey] && 
        (this.state.notificationDetails[cacheKey].pushNotificationTitle !== undefined || 
         this.state.notificationDetails[cacheKey].isLoading ||
         this.state.notificationDetails[cacheKey].error)
    ) {
      return;
    }

    this.setState(prevState => ({
      notificationDetails: {
        ...prevState.notificationDetails,
        [cacheKey]: { isLoading: true }
      }
    }));

    try {
      const endpoint = itemType === 'Survey' ? `surveys/${itemId}` : `discussions/${itemId}`;
      const response = await apiService({}).get(endpoint);
      this.setState(prevState => ({
        notificationDetails: {
          ...prevState.notificationDetails,
          [cacheKey]: {
            pushNotificationTitle: response.data.pushNoteTitle,
            pushNotificationEnabled: response.data.pushNoteEnabled,
            isLoading: false
          }
        }
      }));
    } catch (error) {
      console.error(`Failed to fetch notification details for ${itemType} ${itemId}:`, error);
      this.setState(prevState => ({
        notificationDetails: {
          ...prevState.notificationDetails,
          [cacheKey]: { isLoading: false, error: true }
        }
      }));
    }
  };

  async loadTotalAnswers(data) {
    // This function seems to set component-level loading, ensure it's still intended
    this.setState({ loading: true }); // Potentially conflicts with main isLoading from props
    if (!data) {
      data = this.props.surveyData;
    }
    const totalAnswers = {}; // This is re-declared, likely want to use this.state.totalAnswers or update it
    
    if (!data || !data.content) {
      this.setState({ isLoadingAnswers: false, loading: false });
      return;
    }

    // Assuming the actual logic for fetching answers is more complex or was simplified.
    // For now, just setting state based on what was provided.
    // If this.props.surveyData.content already has answer counts, map them here.
    // Example: if survey items have 'answersCount'
    // data.content.forEach(survey => { totalAnswers[survey.id] = survey.answersCount || 0; });

    this.setState({ totalAnswers, isLoadingAnswers: false, loading: false });
  }

  onSearch = debounce((e) => {
    this.setState({ term: e.target.value });
    this.search({ term: e.target.value, page: 0 });
  }, 200);

  onPerPageChange = (perPage) => {
    this.search({ size: perPage });
    this.setState({ perPage });
  };

  onPageChange = (page) => {
    this.search({ page });
  };

  onStatusFilterChange = (options) => {
    const values = options ? options.map(opt => opt.value) : [];
    this.setState({ statusFilter: values }, () => this.search({ page: 0 }));
  };

  onTypeFilterChange = (options) => {
    const values = options ? options.map(opt => opt.value) : [];
    this.setState({ typeFilter: values }, () => this.search({ page: 0 }));
  };

  onAgentFilterChange = (options) => {
    const values = options ? options.map(opt => opt.value) : [];
    this.setState({ agentFilter: values }, () => this.search({ page: 0 }));
  };

  onTimeFilterChange = (option) => {
    this.setState({ timeFilter: option, customRange: { from: null, to: null } }, () => this.search({ page: 0 }));
  };

  onCustomRangeChange = (from, to) => {
    this.setState({ customRange: { from, to }, timeFilter: TIME_OPTIONS.find(opt => opt.value === 'custom') }, () => this.search({ page: 0 }));
  };

  clearAllFilters = () => {
    const defaultFilters = {
      statusFilter: [],
      typeFilter: [],
      agentFilter: [],
      timeFilter: { label: 'Current week', value: 'current_week' },
      customRange: { from: null, to: null }
    };
    this.setState(defaultFilters, () => {
      localStorage.removeItem('planningFilters');
      this.search({ page: 0 });
    });
  };

  getCurrentSortType = () => {
    return this.state.sort.split(',')[1];
  };

  onChangeSort = (colName) => {
    const currentSort = this.getCurrentSortType();
    let sortType = currentSort;
    if (this.isActiveColSort(colName)) {
      sortType = currentSort === 'asc' ? 'desc' : 'asc';
    }

    const sort = `${colName},${sortType}`;
    this.setState({ sort }, () => this.search({ sort })); // Search will refetch and re-combine
  };

  search = (props) => {
    this.setState({loading: true}); // Indicate loading for the main list
    let dateFrom = undefined;
    let dateTo = undefined;
    const now = new Date();
    switch (this.state.timeFilter.value) {
      case 'next_week':
        dateFrom = new Date(now);
        dateTo = new Date(now); dateTo.setDate(now.getDate() + 7);
        break;
      case 'next_3_days':
        dateFrom = new Date(now);
        dateTo = new Date(now); dateTo.setDate(now.getDate() + 3);
        break;
      case 'today':
        dateFrom = new Date(new Date().setHours(0,0,0,0)); // Corrected 'now' usage
        dateTo = new Date(new Date().setHours(23,59,59,999)); // Corrected 'now' usage
        break;
      case 'current_week':
        dateFrom = new Date(now); dateFrom.setDate(now.getDate() - 3);
        dateTo = new Date(now); dateTo.setDate(now.getDate() + 3);
        break;
      case 'last_3_days':
        dateFrom = new Date(now); dateFrom.setDate(now.getDate() - 3);
        dateTo = new Date(now);
        break;
      case 'last_week':
        dateFrom = new Date(now); dateFrom.setDate(now.getDate() - 7);
        dateTo = new Date(now);
        break;
      case 'custom':
        dateFrom = this.state.customRange.from;
        dateTo = this.state.customRange.to;
        break;
      default:
        break;
    }
    const searchParams = { // Renamed 'search' to 'searchParams' to avoid conflict with function name
      page: this.props.surveyData?.number || 0,
      size: this.state.perPage,
      term: this.state.term,
      sort: this.state.sort,
      status: this.state.statusFilter,
      type: this.state.typeFilter, // Backend filtering for type
      // agent: this.state.agentFilter, // Assuming backend supports agent filter
      dateFrom: dateFrom ? dateFrom.toISOString().slice(0, 10) : undefined,
      dateTo: dateTo ? dateTo.toISOString().slice(0, 10) : undefined,
      ...props,
    };
    
    // Only fetch types that are not filtered out, if typeFilter is active
    const shouldFetchSurveys = !this.state.typeFilter.length || this.state.typeFilter.includes('Survey');
    const shouldFetchChatboxes = !this.state.typeFilter.length || this.state.typeFilter.includes('Chatbox');

    if (shouldFetchSurveys) {
      this.props.fetchSurveys({ search: searchParams });
    } else if (this.props.surveyData?.content?.length) { // Clear survey data if filtered out
        this.props.fetchSurveys({ search: {...searchParams, size: 0, content: []} }); // Or dispatch a clear action
    }

    if (shouldFetchChatboxes) {
      this.props.fetchDiscussions({ search: searchParams });
    } else if (this.props.discussionData?.content?.length) { // Clear discussion data if filtered out
        this.props.fetchDiscussions({ search: {...searchParams, size: 0, content: []} }); // Or dispatch a clear action
    }
    
    // Set loading to false after fetches are initiated. Actual data loading is handled by redux states.
    // This component 'loading' might be for UI elements it controls directly.
    // Consider removing this.state.loading if surveyStates.isLoading and discussionStates.isLoading cover it.
    // For now, let's assume it's for the entire component before redux state kicks in.
    if (!shouldFetchSurveys && !shouldFetchChatboxes) {
        this.setState({ loading: false, combinedData: [] }); // If nothing to fetch, stop loading, clear data
    } else {
        // Loading will be set to false once combineAndSortData is called after data arrives
    }
  };

  combineAndSortData = () => {
    const { surveyData, discussionData } = this.props;
    const { totalAnswers, notificationDetails } = this.state;

    let combinedRawData = [];

    if (surveyData?.content) {
      combinedRawData = [
        ...combinedRawData,
        ...surveyData.content.map(survey => {
          const cacheKey = `survey-${survey.id}`;
          const details = notificationDetails[cacheKey] || { isLoading: true }; // Default to loading
          return {
            ...survey,
            type: 'Survey',
            answersNumber: totalAnswers[survey.id] || 0,
            pushNotificationTitle: details.pushNotificationTitle,
            pushNotificationEnabled: details.pushNotificationEnabled,
            isNotificationLoading: details.isLoading,
            notificationError: details.error,
          };
        })
      ];
    }

    if (discussionData?.content) {
      combinedRawData = [
        ...combinedRawData,
        ...discussionData.content.map(discussion => {
          const cacheKey = `chatbox-${discussion.id}`;
          const details = notificationDetails[cacheKey] || { isLoading: true }; // Default to loading
          return {
            ...discussion,
            type: 'Chatbox',
            answersNumber: discussion.answersNumber || 0, // Example
            pushNotificationTitle: details.pushNotificationTitle,
            pushNotificationEnabled: details.pushNotificationEnabled,
            isNotificationLoading: details.isLoading,
            notificationError: details.error,
          };
        })
      ];
    }
    
    // Sort by dateFrom in descending order
    combinedRawData.sort((a, b) => {
      const dateA = new Date(a.dateFrom).getTime();
      const dateB = new Date(b.dateFrom).getTime();
      return dateB - dateA; // Highest dates first
    });

    // --- FRONTEND FILTERING ---
    const { statusFilter, typeFilter, term, timeFilter, customRange } = this.state;
    let filtered = combinedRawData;

    if (statusFilter && statusFilter.length > 0) {
      filtered = filtered.filter(item => {
        // Normalize item.status for comparison (e.g. from SURVEY_STATUS_LABEL or direct)
        const itemStatus = item.type === 'Survey' 
            ? SURVEY_STATUS_LABEL[item.status] || item.status 
            : item.status;
        return statusFilter.includes(this.capitalize(itemStatus || ''));
      });
    }
    if (typeFilter && typeFilter.length > 0) {
      filtered = filtered.filter(item => typeFilter.includes(item.type));
    }
    if (term && term.trim().length > 0) {
      const t = term.trim().toLowerCase();
      filtered = filtered.filter(item =>
        (item.title && item.title.toLowerCase().includes(t)) || 
        (item.surveyMonkeyTitle && item.surveyMonkeyTitle.toLowerCase().includes(t)) || 
        (item.description && item.description.toLowerCase().includes(t))
      );
    }

    let dateFromFilter, dateToFilter;
    const now = new Date();
    switch (timeFilter?.value) {
        case 'next_week': dateFromFilter = new Date(now); dateToFilter = new Date(new Date().setDate(now.getDate() + 7)); break;
        case 'next_3_days': dateFromFilter = new Date(now); dateToFilter = new Date(new Date().setDate(now.getDate() + 3)); break;
        case 'today': dateFromFilter = new Date(new Date().setHours(0,0,0,0)); dateToFilter = new Date(new Date().setHours(23,59,59,999)); break;
        case 'current_week': dateFromFilter = new Date(new Date().setDate(now.getDate() - 3)); dateToFilter = new Date(new Date().setDate(now.getDate() + 3)); break;
        case 'last_3_days': dateFromFilter = new Date(new Date().setDate(now.getDate() - 3)); dateToFilter = new Date(now); break;
        case 'last_week': dateFromFilter = new Date(new Date().setDate(now.getDate() - 7)); dateToFilter = new Date(now); break;
        case 'custom': dateFromFilter = customRange.from; dateToFilter = customRange.to; break;
        default: break;
    }

    if (dateFromFilter && dateToFilter) {
      // Ensure time part is considered for accurate range filtering if needed, or normalize dates
      const startOfDayFrom = dateFromFilter ? new Date(new Date(dateFromFilter).setHours(0,0,0,0)) : null;
      const endOfDayTo = dateToFilter ? new Date(new Date(dateToFilter).setHours(23,59,59,999)): null;

      filtered = filtered.filter(item => {
        const itemDate = item.dateFrom ? new Date(item.dateFrom) : null;
        if (!itemDate) return false; // Or true, depending on how you want to handle items without dates
        // Assuming item.dateFrom is start of day, or adjust itemDate comparison as needed
        return itemDate >= startOfDayFrom && itemDate <= endOfDayTo;
      });
    }
    
    // Set loading to false here as data processing is complete.
    this.setState({ combinedData: filtered, loading: false });
  };


  isActiveColSort = (colName) => {
    return colName === this.state.sort.split(',')[0];
  };

  goToSurveyDetails = (type, id) => () => {
    const country = this.props.selectedCountry || 'NL';
    if (type === 'Survey') {
      this.props.history.push(`/app/surveys/edit/${id}?country=${country}`);
    } else if (type === 'Chatbox') {
      this.props.history.push(`/app/discussions/questions/edit/${id}?country=${country}`);
    }
  };

  capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  
  render() {
    const { surveyStates, discussionStates } = this.props;
    const { totalAnswers, combinedData } = this.state; // Removed statusFilter, loading from here as it's used differently

    // isLoading is now primarily for the main list data from redux
    const isListLoading = surveyStates?.isLoading || discussionStates?.isLoading;
    // this.state.loading can be used for overall component readiness including filters/sorts
    if (this.state.loading && !combinedData.length) { // Show loading if explicitly set and no data yet
         return <div>Loading planning data...</div>;
    }
    
    const columns = [
      { label: '#', colName: 'id', sortable: true, width: '50px' },
      { label: 'Type', colName: 'type', sortable: true, width: '80px' },
      { label: 'Title', colName: 'title', sortable: true, width: '250px' },
      { label: 'Notification', colName: 'notification', sortable: false, width: '180px' }, // Increased width
      { label: 'From', colName: 'date_from', sortable: true, width: '120px' },
      { label: 'To', colName: 'date_to', sortable: true, width: '120px' },
      { label: 'Status', colName: 'status', sortable: true, width: '90px' },
      { label: 'Reach', colName: 'reach', sortable: false, width: '70px' },
    ];

    const statusColors = {
      "Live": "#0000FF", "Open": "#DFC107", "Completed": "#00AA87",
      "Incomplete": "#FF803F", "Canceled": "#D62828", "Scheduled": "#808080", "Closed": "#505050"
    };
    
    return (
      <Page>
        <ListHeading title="Planning" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 8px 0', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ width: '180px' }}>
              <Select isMulti options={STATUS_OPTIONS} onChange={this.onStatusFilterChange}
                value={STATUS_OPTIONS.filter(option => this.state.statusFilter.includes(option.value))} placeholder="Status" />
            </div>
            <div style={{ width: '200px' }}>
              <Select isMulti options={TYPE_OPTIONS} onChange={this.onTypeFilterChange}
                value={TYPE_OPTIONS.filter(option => this.state.typeFilter.includes(option.value))} placeholder="Type" />
            </div>
            <div style={{ width: '180px'}}>
              <Select options={TIME_OPTIONS} onChange={this.onTimeFilterChange} value={this.state.timeFilter} placeholder="Time" />
            </div>
            {this.state.timeFilter.value === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="date" style={{ height: 38, border: '1px solid #ced4da', borderRadius: 4, padding: '8px 12px', fontSize: '1rem', background: '#fff', color: '#495057', boxSizing: 'border-box' }}
                  value={this.state.customRange.from ? this.state.customRange.from.toISOString().slice(0,10) : ''}
                  onChange={e => this.onCustomRangeChange(e.target.value ? new Date(e.target.value) : null, this.state.customRange.to)} />
                <span>-</span>
                <input type="date" style={{ height: 38, border: '1px solid #ced4da', borderRadius: 4, padding: '8px 12px', fontSize: '1rem', background: '#fff', color: '#495057', boxSizing: 'border-box' }}
                  value={this.state.customRange.to ? this.state.customRange.to.toISOString().slice(0,10) : ''}
                  onChange={e => this.onCustomRangeChange(this.state.customRange.from, e.target.value ? new Date(e.target.value) : null)} />
              </div>
            )}
            <div style={{ width: '200px' }}>
              <Field component={TextField} name="search" onChange={this.onSearch} placeholder="Type to search" noMargin />
            </div>
          </div>
          <Button type="button" onClick={this.clearAllFilters}>Clear all filters</Button>
        </div>
        <AppContent>
          <div className="row">
            <div className="col-md-12">
              <Table isLoading={isListLoading && !combinedData.length}> {/* Show table loading if redux is loading AND no combined data yet */}
                <Thead>
                  <Tr>
                    {columns.map((col, index) => (
                      <Th key={`col-${col.label}`} onClick={col.sortable ? () => this.onChangeSort(col.colName) : null}
                        currentSortType={this.getCurrentSortType()} active={this.isActiveColSort(col.colName)}
                        sortable={col.sortable} short={index === columns.length - 1}
                        style={{ width: col.width, textAlign: 'left', textTransform: 'uppercase', minWidth: '70px', fontSize: '12px', color: '#7783a1', fontWeight: 600, fill: '#7783a1', overflow: 'hidden', paddingLeft: '5px' }}>
                        {col.label}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {map(combinedData, (item) => {
                    const statusDisplay = this.capitalize(lowerCase( item.type === 'Survey' ? SURVEY_STATUS_LABEL[item.status] || item.status : item.status));
                    const currentStatusColor = statusColors[statusDisplay] || "#000000";

                    let notificationContent: React.ReactNode = '-';
                    if (item.isNotificationLoading) {
                        notificationContent = '...';
                    } else if (item.notificationError) {
                        notificationContent = <span style={{ color: 'red' }}>Error</span>;
                    } else if (item.pushNotificationEnabled) {
                        if (typeof item.pushNotificationTitle === 'object' && item.pushNotificationTitle !== null && item.pushNotificationTitle.NL) {
                            notificationContent = item.pushNotificationTitle.NL;
                        } else if (typeof item.pushNotificationTitle === 'string') {
                            notificationContent = item.pushNotificationTitle;
                        } else {
                            notificationContent = <em>-</em>;
                        }
                    }

                    const titleToDisplay = item.type === 'Survey' ? (item.surveyMonkeyTitle || item.title || '') : (item.title || '');

                    const reach = item.type === 'Survey'
                      ? `${totalAnswers[item.id] || 0}/${item.respondentsLimit || '0'}`
                      : `${item.answersNumber || 0}/${item.respondentsLimit || '0'}`;

                    return (
                      <Tr key={`${item.type.toLowerCase()}-${item.id}`} onClick={this.goToSurveyDetails(item.type, item.id)}>
                        <Td style={{ width: columns[0].width }}>{item.id}</Td>
                        <Td style={{ width: columns[1].width }}>{item.type}</Td>
                        <Td style={{ width: columns[2].width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={titleToDisplay}>{titleToDisplay}</Td>
                        <Td style={{ width: columns[3].width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {notificationContent}
                        </Td>
                        <Td style={{ width: columns[4].width }}>{dateToReadable(utcDateToLocal(item.dateFrom))}</Td>
                        <Td style={{ width: columns[5].width }}>{dateToReadable(utcDateToLocal(item.dateTo))}</Td>
                        <Td style={{ width: columns[6].width }}>
                          <span className={styles.statusCircle} style={{ backgroundColor: currentStatusColor }}></span>
                          {statusDisplay}
                        </Td>
                        <Td style={{ width: columns[7].width }}>{reach}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              <Pagination
                onPageChange={this.onPageChange}
                pages={(this.props.surveyData?.totalPages || this.props.discussionData?.totalPages || 1) -1 } // Adjust for combined pagination
                currentPage={this.props.surveyData?.number || this.props.discussionData?.number || 0} // Adjust
                visibleOffset={1}
                perPage={this.state.perPage}
                onPerPageChange={this.onPerPageChange}
              />
            </div>
          </div>
        </AppContent>
      </Page>
    );
  }
}

const mapStateToProps = ({ surveys, discussions, ...state }) => ({
  surveyData: surveys.list.data,
  surveyStates: surveys.list.states,
  discussionData: discussions.list.data,
  discussionStates: discussions.list.states,
  formValues: getFormValues(FORM_NAME)(state),
  selectedCountry: state.app.country,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ 
  fetchSurveys,
  fetchDiscussions
}, dispatch);

export default compose<any>(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({ form: FORM_NAME, destroyOnUnmount: false })
)(List);